"use client"

import { observable } from "@legendapp/state"
import { useSelector } from "@legendapp/state/react"
import type { TLoginUserData } from "@/schemas/user.schema"

type TIntercomStatus = "not-initialized" | "initializing" | "ready" | "error"

type IntercomModule = typeof import("@intercom/messenger-js-sdk")

let intercomModulePromise: Promise<IntercomModule> | null = null
let intercomModule: IntercomModule | null = null

async function loadIntercomModule() {
  if (!intercomModulePromise) {
    intercomModulePromise = import("@intercom/messenger-js-sdk").then((module) => {
      intercomModule = module
      return module
    })
  }

  return intercomModulePromise
}

interface IntercomData {
  status: TIntercomStatus
  isChatOpen: boolean
  unreadCount: number
}

const initialState: IntercomData = {
  status: "not-initialized",
  isChatOpen: false,
  unreadCount: 0,
}

const intercomStore$ = observable(initialState)

export function useIntercom() {
  const isIntercomReady = useSelector(() => {
    switch (intercomStore$.status.get()) {
      case "ready":
        return true
      case "error":
        return false
      case "initializing":
        return false
      case "not-initialized":
        return false
      default:
        return false
    }
  })

  const intercomStatus = useSelector(() => intercomStore$.status.get())
  const intercomChatOpen = useSelector(() => intercomStore$.isChatOpen.get())
  const intercomUnreadCount = useSelector(() => intercomStore$.unreadCount.get())

  /**
   * Shuts down the Intercom service if it is currently ready.
   * This function will:
   * - Call the `shutdownIntercom` function to stop the Intercom service.
   * - Update the `intercomStore$` status to "not-initialized".
   * - Set the `isChatOpen` state to false.
   */
  function shutdown() {
    if (intercomStatus === "ready") {
      void loadIntercomModule().then((module) => {
        module.shutdown()
      })
      intercomStore$.status.set("not-initialized")
      intercomStore$.isChatOpen.set(false)
    }
  }

  /**
   * Opens the Intercom chat if the Intercom status is "ready".
   * If the chat is successfully opened, it updates the chat open status in the store.
   */
  function open() {
    if (intercomStatus === "ready") {
      void loadIntercomModule().then((module) => {
        module.show()
      })
    }
  }

  /**
   * Closes the Intercom chat if it is currently ready.
   *
   * This function checks the status of the Intercom chat. If the status is "ready",
   * it hides the Intercom chat and updates the `isChatOpen` state in the `intercomStore$` to `false`.
   */
  function close() {
    if (intercomStatus === "ready") {
      void loadIntercomModule().then((module) => {
        module.hide()
      })
    }
  }

  /**
   * Initializes the Intercom service with the provided user data and user hash.
   *
   * @param {Object} params - The parameters for initialization.
   * @param {TLoginUserData} [params.userData] - The user data required for Intercom initialization.
   * @param {string} [params.userHash] - The user hash required for Intercom initialization.
   *
   * @returns {void}
   *
   * @remarks
   * - If `userData` is not provided, the Intercom status is set to "not-initialized".
   * - If the Intercom status is "not-initialized", it proceeds with the initialization.
   * - The Intercom App ID is retrieved from the environment variables.
   * - If both the Intercom App ID and `userHash` are available, it initializes Intercom with the provided user data.
   * - Sets up a listener for unread message count changes.
   * - Updates the Intercom status to "ready" upon successful initialization.
   * - Logs an error and sets the Intercom status to "error" if the Intercom App ID is not set.
   */
  function initialize({ userData, userHash }: { userData?: TLoginUserData; userHash?: string }) {
    if (!userData) {
      intercomStore$.status.set("not-initialized")
      return
    }

    if (intercomStatus === "not-initialized") {
      const intercomAppID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID

      intercomStore$.status.set("initializing")

      if (intercomAppID && userHash) {
        void loadIntercomModule()
          .then((module) => {
            module.default({
              app_id: intercomAppID,
              hide_default_launcher: true,
              user_id: userData.user.id.toString(),
              user_hash: userHash,
              name: userData.user.name,
              email: userData.user.email,
              account_id: userData.user.accountId,
              account_state: userData.user.accountState,
              channel: userData.user.accountSignupChannel,
              created_at: userData.user.dateCreated,
              user_count: userData.user.accountSeats,
              crm_user_count: userData.user.crmSeats,
              supervisor: userData.user.isAdmin,
              account_owner: userData.user?.isOwner,
              user_call_minutes_last30days: userData.user.callMinutesLast30Days,
              account_call_minutes_last30days: userData.user.accountCallMinutesLast30Days,
              user_dialer_sessions_last30days: userData.user.dialingSessionsLast30Days,
              account_dialer_sessions_last30days: userData.user.accountDialingSessionsLast30Days,
              sms_enabled: userData.user.accountSmsEnabled,
              vmdrop_setup: userData.user.vmdropSetup,
              account_trial_type: userData.user.accountTrialType,
              account_activation_status: userData.user.accountTrialActivated ? "Activated" : "Limited",
              account_email_marketing_opt_in: userData.user.emailMarketingOptIn,
              account_sms_opt_in: userData.user.smsOptIn,
              account_activation_hash: userData.user.rg_acct_hash,
              alignment: "left",
            })

            module.onUnreadCountChange((unreadCount: unknown) => {
              intercomStore$.unreadCount.set(Number.parseInt(`${unreadCount}`, 10))
            })

            module.onHide(() => {
              intercomStore$.isChatOpen.set(false)
            })

            module.onShow(() => {
              intercomStore$.isChatOpen.set(true)
            })

            intercomStore$.status.set("ready")
          })
          .catch(() => {
            intercomStore$.status.set("error")
          })
      } else {
        console.error("Intercom App ID is not set")
        intercomStore$.status.set("error")
      }
    }
  }

  return {
    isReady: isIntercomReady,
    isChatOpen: intercomChatOpen,
    status: intercomStatus,
    unreadCount: intercomUnreadCount,
    initialize,
    shutdown,
    open,
    close,
  }
}
